/**
 * emotionalSensitivityDetector.js
 * Classifies the dominant emotional state in a user message and
 * returns behavioral adjustments for pacing, warmth, and density.
 */

const EMOTION_PATTERNS = {
  grief: [
    /due[lo]+|pérdida|perdida\s+de\s|murió|falleci[oó]|muerte\s+de|luto|mi\s+(?:esposo|esposa|hijo|hija|padre|madre|hermano|hermana)\s+(?:murió|falleció)/i,
  ],
  fear: [
    /miedo|temor|angustia(?!\s+de\s+la)|ansied[a-z]+|pánico|ataque\s+de\s+pánico|me\s+da\s+miedo|tengo\s+miedo|no\s+s[eé]\s+qué\s+va\s+a\s+pasar/i,
  ],
  shame: [
    /vergüenza|vergonz|me\s+siento\s+(?:culpable|mal|sucio)|fallé\s+como|no\s+(?:sirvo|merezco|soy\s+digno)|pecado\s+que\s+me/i,
  ],
  loneliness: [
    /solo\b(?!\s+(?:que|un|una|hay))|soledad|nadie\s+me\s+(?:entiende|apoya|ayuda)|sin\s+apoyo|abandonado|me\s+siento\s+solo/i,
  ],
  exhaustion: [
    /agotad[ao]|cansad[ao]\s+(?:de|ya)|ya\s+no\s+puedo\s+m[aá]s|burn.?out|estresado\s+al\s+m[aá]ximo|no\s+doy\s+m[aá]s|quiero\s+renunciar\s+al\s+ministerio/i,
  ],
  discouragement: [
    /desanimad[ao]|frustrad[ao]|rendirme|no\s+vale\s+la\s+pena|sin\s+esperanza|desesperado\s+con|no\s+tiene\s+sentido/i,
  ],
  conflict: [
    /conflicto\s+(?:con|en\s+la)|pelea\s+(?:con|en)|enfrentamiento|tensión\s+(?:con|en\s+la\s+iglesia)|discusión\s+fuerte/i,
  ],
  spiritual_confusion: [
    /dudas\s+de\s+(?:fe|dios)|no\s+entiendo\s+a\s+dios|crisis\s+de\s+fe|dios\s+me\s+abandon[oó]|ya\s+no\s+(?:creo|siento\s+a\s+dios)|Dios\s+no\s+responde/i,
  ],
  urgency: [
    /urgente|ahora\s+mismo|inmediatamente|esto\s+es\s+una\s+emergencia|necesito\s+ayuda\s+ya/i,
  ],
};

// Behavioral adjustments per dominant emotion
const ADJUSTMENTS = {
  grief:              { verbosity: "reduced", warmth: "high",   directness: "low",    scriptureDensity: "low",   recommendationDensity: "low"   },
  fear:               { verbosity: "reduced", warmth: "high",   directness: "low",    scriptureDensity: "low",   recommendationDensity: "low"   },
  shame:              { verbosity: "reduced", warmth: "high",   directness: "low",    scriptureDensity: "normal", recommendationDensity: "low"  },
  loneliness:         { verbosity: "normal",  warmth: "high",   directness: "normal", scriptureDensity: "normal", recommendationDensity: "normal"},
  exhaustion:         { verbosity: "reduced", warmth: "high",   directness: "low",    scriptureDensity: "low",   recommendationDensity: "low"   },
  discouragement:     { verbosity: "normal",  warmth: "high",   directness: "normal", scriptureDensity: "normal", recommendationDensity: "low"  },
  conflict:           { verbosity: "normal",  warmth: "normal", directness: "normal", scriptureDensity: "normal", recommendationDensity: "normal"},
  spiritual_confusion:{ verbosity: "expanded",warmth: "high",   directness: "low",    scriptureDensity: "normal", recommendationDensity: "low"  },
  urgency:            { verbosity: "reduced", warmth: "normal", directness: "high",   scriptureDensity: "low",   recommendationDensity: "high"  },
};

const DEFAULT_ADJUSTMENTS = {
  verbosity: "normal", warmth: "normal", directness: "normal",
  scriptureDensity: "normal", recommendationDensity: "normal",
};

/**
 * @param {string} text
 * @returns {{
 *   detected: boolean,
 *   dominantEmotion: string | null,
 *   intensity: "low" | "medium" | "high",
 *   adjustments: object,
 * }}
 */
export function detectEmotionalSensitivity(text) {
  if (!text?.trim()) return { detected: false, dominantEmotion: null, intensity: "low", adjustments: DEFAULT_ADJUSTMENTS };

  const matches = [];
  for (const [emotion, patterns] of Object.entries(EMOTION_PATTERNS)) {
    if (patterns.some((p) => p.test(text))) {
      matches.push(emotion);
    }
  }

  if (matches.length === 0) {
    return { detected: false, dominantEmotion: null, intensity: "low", adjustments: DEFAULT_ADJUSTMENTS };
  }

  // Intensity: more matches = higher intensity signal
  const intensity = matches.length >= 3 ? "high" : matches.length >= 2 ? "medium" : "low";

  // Priority order for dominant emotion (crisis states first)
  const PRIORITY = ["grief", "urgency", "fear", "shame", "exhaustion", "discouragement", "loneliness", "conflict", "spiritual_confusion"];
  const dominant = PRIORITY.find((e) => matches.includes(e)) ?? matches[0];

  return {
    detected:       true,
    dominantEmotion: dominant,
    intensity,
    allDetected:    matches,
    adjustments:    ADJUSTMENTS[dominant] ?? DEFAULT_ADJUSTMENTS,
  };
}
